/**
 * Shared behaviour of the two blocking overlays, `Modal` and `admin/Drawer`.
 *
 * - Registers a layer (`lib/layers.ts`) so only the top overlay hears Escape.
 * - Escape inside a non-empty text field first leaves the field (blur); the
 *   next Escape closes. Same reason as the backdrop guard below: these panels
 *   host half-written answers, and one stray key must not discard them.
 * - Backdrop closes only when the whole click happened on the backdrop *and*
 *   this overlay is on top. A text selection dragged out of a textarea
 *   otherwise reads as a backdrop click.
 * - Focus is trapped inside the panel and returned on close.
 */
import { useId, useRef, type MouseEvent, type RefObject } from "react";

import { isDismissGesture, useLayer, type LayerKind } from "../layers";
import { useFocusTrap } from "./useFocusTrap";

interface Options {
  open: boolean;
  onClose: () => void;
  kind: LayerKind;
  closeOnEsc?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

function isFilledTextField(el: Element | null): el is HTMLElement {
  if (el instanceof HTMLTextAreaElement) return el.value.trim().length > 0;
  if (el instanceof HTMLInputElement) {
    const textual = ["text", "search", "email", "url", "tel", "password", ""].includes(el.type);
    return textual && el.value.trim().length > 0;
  }
  return el instanceof HTMLElement && el.isContentEditable && (el.textContent ?? "").trim().length > 0;
}

export function useDialog({ open, onClose, kind, closeOnEsc = true, initialFocusRef }: Options) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const downOnBackdrop = useRef(false);
  const titleId = useId();

  const isTop = useLayer(open, kind, () => {
    const active = document.activeElement;
    if (panelRef.current?.contains(active) && isFilledTextField(active)) {
      active.blur();
      return;
    }
    if (closeOnEsc) onClose();
  });

  useFocusTrap(panelRef, open, {
    initialFocus: initialFocusRef ?? "first",
    isTop,
  });

  const backdropProps = {
    onMouseDown: (e: MouseEvent) => {
      downOnBackdrop.current = e.target === e.currentTarget && !isDismissGesture(e.target);
    },
    onClick: (e: MouseEvent) => {
      if (e.target === e.currentTarget && downOnBackdrop.current && isTop()) onClose();
    },
  };

  return { panelRef, titleId, backdropProps, isTop };
}
